import { Link } from 'react-router-dom';
import { Button } from '@/kit';
import { hasPassportAuth, hasSupabase } from '@/config/env';
import { useMeStore } from '@/store/meStore';
import { useAuth } from '@/auth/AuthProvider';
import { lockPin } from '@/auth/pinGate';
import { routes } from '@/config/routes';

export function SettingsPage() {
  const hottie = useMeStore((s) => s.hottiePermission);
  const setHottieEnabled = useMeStore((s) => s.setHottieEnabled);
  const setFlag = useMeStore((s) => s.setHottieShareFlag);
  const getHottieContext = useMeStore((s) => s.getHottieContext);
  const resetLocal = useMeStore((s) => s.resetLocal);
  const identity = useMeStore((s) => s.identity);
  const profile = useMeStore((s) => s.profile);
  const { user, logOut, logIn, busy, passportReady } = useAuth();

  const preview = getHottieContext();

  return (
    <div className="space-y-10">
      <header>
        <p className="text-[10px] font-extrabold uppercase tracking-hottr-stamp text-hottr-pink">
          Control
        </p>
        <h1 className="mt-1 font-display text-3xl font-black uppercase tracking-hottr-display">
          Settings
          <span className="text-hottr-pink">.</span>
        </h1>
      </header>

      <section className="space-y-3">
        <h2 className="text-[10px] font-extrabold uppercase tracking-hottr-stamp text-white/48">
          Account
        </h2>
        {user ? (
          <div className="space-y-3 rounded-sm border border-white/12 px-4 py-4">
            <p className="text-[13px] text-white/72">
              Signed in with your HOTTR account
              <span className="text-hottr-pink">.</span>
            </p>
            <p className="break-all text-[12px] text-white/55">{user.email ?? user.id}</p>
            <Button variant="secondary" disabled={busy} onClick={() => void logOut()}>
              Sign out
            </Button>
          </div>
        ) : (
          <div className="space-y-3 rounded-sm border border-white/12 px-4 py-4">
            <p className="text-[13px] text-white/55">
              {passportReady
                ? 'Sign in with the same HOTTR ID used on .tv, .app, and the rest of the ecosystem.'
                : 'Account sign-in isn’t available right now.'}
            </p>
            <Button
              variant="primary"
              disabled={!passportReady || busy}
              onClick={() => void logIn(routes.settings)}
            >
              Sign in with your HOTTR account
            </Button>
            <Link to={routes.auth} className="block text-[12px] text-hottr-pink hover:underline">
              Open auth page
            </Link>
          </div>
        )}
        <p className="text-[13px] text-white/55">
          Identity mode: <span className="text-white">{identity.mode}</span>
          {hasSupabase() ? ' · data project configured' : ' · local data store'}
          {hasPassportAuth() ? ' · account ready' : ''}
        </p>
        <p className="break-all text-[12px] text-white/40">User: {identity.userId}</p>
        {profile && (
          <p className="text-[13px] text-white/55">
            Focus: {profile.focusAreaIds.join(', ')} · Style: {profile.helpStyle}
          </p>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-[10px] font-extrabold uppercase tracking-hottr-stamp text-white/48">
          Hottie context
        </h2>
        <p className="text-sm text-white/55">
          Hottie only sees HOTTR.me data when you explicitly allow it. Off by default.
        </p>
        <label className="flex items-center justify-between rounded-sm border border-white/12 px-4 py-3">
          <span className="text-[13px] font-semibold">Share with Hottie</span>
          <input
            type="checkbox"
            checked={hottie.enabled}
            onChange={(e) => setHottieEnabled(e.target.checked)}
            className="h-5 w-5 accent-[#FF0CB6]"
          />
        </label>
        {(
          [
            ['shareFocusAreas', 'Focus areas'],
            ['shareActiveBreakout', 'Active Breakout'],
            ['shareGoals', 'Goals'],
            ['shareMomentum', 'Momentum'],
            ['shareTriggers', 'Triggers'],
            ['shareNudgePrefs', 'Nudge style'],
          ] as const
        ).map(([flag, label]) => (
          <label
            key={flag}
            className="flex items-center justify-between rounded-sm border border-white/10 px-4 py-3"
          >
            <span className="text-[13px] text-white/72">{label}</span>
            <input
              type="checkbox"
              disabled={!hottie.enabled}
              checked={hottie[flag]}
              onChange={(e) => setFlag(flag, e.target.checked)}
              className="h-5 w-5 accent-[#FF0CB6]"
            />
          </label>
        ))}
        <div className="rounded-sm border border-white/10 bg-white/[0.02] p-3">
          <p className="text-[10px] font-extrabold uppercase tracking-hottr-label text-white/40">
            Context preview (what Hottie would receive)
          </p>
          <pre className="mt-2 overflow-x-auto font-mono text-[11px] text-white/55">
            {preview === null
              ? 'null — sharing disabled'
              : JSON.stringify(preview, null, 2)}
          </pre>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-[10px] font-extrabold uppercase tracking-hottr-stamp text-white/48">
          Privacy
        </h2>
        <p className="text-sm text-white/55">
          Behaviour, sexual-health-adjacent, and substance-related data are treated as sensitive.
          See docs/PRIVACY.md and docs/SAFETY.md in the repo.
        </p>
      </section>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          variant="secondary"
          onClick={() => {
            lockPin();
            window.location.href = '/';
          }}
        >
          Lock PIN (24h gate)
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            if (window.confirm('Reset all local HOTTR.me data on this device?')) {
              resetLocal();
              window.location.href = '/';
            }
          }}
        >
          Reset local data
        </Button>
      </div>
    </div>
  );
}
